<template>
  <v-container fluid class="Statistics">
    <v-layout>
      <template v-if="!isAbnormalStatistics">
        <v-flex md2>
          <v-select
            :label="$t('dateType')"
            v-model="mode"
            small
            item-text="label"
            item-value="value"
            :items="dateTypes"
            :return-object="false"
          ></v-select>
        </v-flex>
        <v-flex md2 v-if="mode === 'month'">
          <DatePicker key="month" v-model="month" mode="month" />
        </v-flex>
      </template>
      <v-flex md2 v-if="mode === 'date'">
        <DatePicker 
          key="date" 
          v-model="date" 
          mode="date"
          :range="true"
          :max="dateMax"
          :min="dateMin"
        >
          <template #default>
            <v-btn flat color="primary" @click="setDate(7)">
              近一周
            </v-btn>
            <v-btn flat color="primary" @click="setDate(30)">
              近30天
            </v-btn>
            <v-btn flat color="primary" v-if="isAbnormalStatistics" @click="setDate(-1)">
              全部
            </v-btn>
          </template>
        </DatePicker>
      </v-flex>
      <v-flex md1></v-flex>
      <v-flex md1>
        <LoaderButton 
          :loading="loading"
          :buttonText="$t('search')"
          :promiseAwait="getData"
        >
        </LoaderButton>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex md12>
        <v-chart
          :options="leftChartOption"
          :init-options="initOptions"
          theme="ovilia-green"
          autoresize />
      </v-flex>
      <v-flex md12>
        <v-chart
          :options="rightChartOption"
          :init-options="initOptions"
          theme="ovilia-green"
          autoresize />
      </v-flex>
    </v-layout>
    <v-layout v-if="!isAbnormalStatistics" justify-end align-center>
      <v-btn color="primary" @click.stop="exportFile">
        {{ $t('export') }}
      </v-btn>
    </v-layout>
    <v-layout>
      <v-flex md22>
        <v-data-table
          :headers="headers"
          :items="targetData"
          :pagination.sync="pagination"
          class="table"
        >
          <template v-slot:items="props">
            <tr v-if="isAbnormalStatistics" @click="showDetails(props.item)" style="cursor: pointer">
              <td>{{ props.item.channel }}</td>
              <td>{{ props.item.invCount }}</td>
              <!-- <td>{{ props.item.manager }}</td> -->
              <td v-for="(row, i) in headers.slice(2)" :key="i">
                {{
                  row.format
                    ? row.format(props.item, row)
                    : props.item[row.value] || 0
                }}
              </td>
            </tr>
            <tr v-else>
              <td>{{ props.item.clientName }}</td>
              <td style="width: 300px">{{ props.item.department }}</td>
              <td>{{ props.item.manager }}</td>
              <td v-for="(row, i) in headers.slice(3)" :key="i">
                ${{ dataTypes.map(item => formatTableCell(props.item, row, item)).join(' / ') }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <TablePopup
      v-if="isAbnormalStatistics"
      v-model="detailPopup.show"
      @popupClose="$emit('input', detailPopup.show = false)"
      :title="`${detailPopup.channel} ${$t('detail')}`"
      hideRgtBtn
      large
      class="table"
    >
      <template v-slot:input>
        <v-container>
          <v-data-table
            :headers="detailHeaders"
            :items="detailPopup.data"
            :pagination.sync="detailPagination"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.clientName }}</td>
                <td>{{ props.item.department }}</td>
                <td>{{ props.item.manager }}</td>
                <td v-for="(row, i) in detailHeaders.slice(3)" :key="i">
                  {{
                    row.format
                      ? row.format(props.item, row)
                      : props.item[row.value] || 0
                  }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-container>
      </template>
    </TablePopup>
  </v-container>
</template>

<script>
import LoaderButton from './LoaderButton'
import DatePicker from './DatePicker'
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/pie'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/title'
import 'echarts/lib/component/tooltip'
import { toMoney, exportExcelFile, toPickerDateString } from '@/utils/tools'
import { mapGetters } from 'vuex'
// import { LabelStatistics } from '@/orm/models/LabelStatistics'
export default {
  name: 'Statistics',
  components: {
    'v-chart': ECharts,
    LoaderButton,
    DatePicker
  },
  data () {
    return {
      isAbnormalStatistics: false,
      mode: 'month',
      range: false,
      organization: '',
      date: this.getRangeDate(30, new Date(new Date().getTime() - 60 * 60 * 24 * 1000)),
      month: new Date(),
      initOptions: {
        renderer: 'canvas'
      },
      pagination: {
        rowsPerPage: 10,
        sortBy: 'total_expense',
        descending: true
      },
      dataTypes: ['expense', 'count'],
      statisticsDataTypes: ['expense', 'cost', 'count'],
      statistics: [],
      loading: false
    }
  },
  computed: {
    systemBalanceMap () {
      return this.systemBalances.reduce((res, item) => {
        const { clientKey } = item
        res[clientKey] = item
        return res
      }, {})
    },
    isDate () {
      return this.mode === 'date'
    },
    dateTypes () {
      return [
        { label: this.$t('date'), value: 'date' },
        { label: this.$t('month'), value: 'month' }
      ]
    },
    allStatisticsData () {
      return this.$store.getters.labelStatistics || {}
    },
    targetData () {
      return this.curDailyStatisticsData
    },
    curStatisticsData () {
      const data = this.allStatisticsData[this.month.toISOString().slice(0, 7)] || []
      const result = data.map(item => {
        const systemBalance = this.systemBalanceMap[item.clientKey]
        return {
          ...item,
          manager: systemBalance?.accountManager || '',
          department: systemBalance?.department || ''
        }
      })
      return result
    },
    curDailyStatisticsData () {
      const data = this.statistics.map(item => {
        const { clientKey, clientName, details } = item
        const systemBalance = this.systemBalanceMap[clientKey]
        const targetStatistics = details.reduce((res, detail) => {
          const { statistics, statName, provider, type } = detail
          const { expense, cost, count } = statistics
          if (!type.includes('label')) return res
          const [expenseName, costName, countName] = this.statisticsDataTypes.map(type => {
            if (statName === 'GENERAL') {
              return `${provider}_${type}`
            }
            const regExp = new RegExp(/_V\d/)
            const name = statName.replace(regExp, '')
            return `${name}_${type}`
          })
          if (type.includes('cancel')) {
            res[expenseName] = (res[expenseName] || 0) - expense
            res[costName] = (res[costName] || 0) - cost
            res[countName] = (res[countName] || 0) - count
            res['total_expense'] -= expense
            res['total_cost'] -= cost
            res['total_count'] -= count
          } else {
            const isInvoice = type.includes('invoice')
            const actualCost = isInvoice ? 0 : cost
            res[expenseName] = (res[expenseName] || 0) + expense
            res[costName] = (res[costName] || 0) + actualCost
            res[countName] = (res[countName] || 0) + (isInvoice ? 0 : count)
            res['total_expense'] += expense
            res['total_cost'] += actualCost
            res['total_count'] += isInvoice ? 0 : count
          }
          return res
        }, {
          total_expense: 0,
          total_cost: 0,
          total_count: 0
        })
        return {
          clientKey,
          clientName,
          manager: systemBalance?.accountManager || '',
          department: systemBalance?.department || '',
          ...targetStatistics
        }
      })
      return data
    },
    leftChartOption () {
      return this.getCommonOptions({
        title: 'count',
        seriesName: 'count',
        data: this.sortLeftData
      })
    },
    rightChartOption () {
      return this.getCommonOptions({
        title: 'expense',
        seriesName: 'expense',
        data: this.sortRightData
      })
    },
    sortLeftData () {
      const allData = this.sortData(
        this.targetData,
        'total_count'
      )
      return this.getRankAndOtherData(allData, 'total_count')
    },
    sortRightData () {
      const allData = this.sortData(
        this.targetData,
        'total_expense'
      )
      return this.getRankAndOtherData(allData, 'total_expense')
    },
    headers () {
      // if (!this.isAbnormalStatistics) {
      //   return this.$store.getters.labelStatisticHeaders
      // }
      const headers = this.targetData.reduce((res, item) => {
        Object.keys(item).forEach(key => {
          if (this.statisticsDataTypes.some(type => key.includes(type))) {
            const statNameArr = key.split('_')
            statNameArr.pop()
            const statName = statNameArr.join('_')
            if (statName !== 'total') res.push(statName)
          }
        })
        return res
      }, [])
      return [
        {
          sortable: false,
          text: this.$t('organizationName'),
          value: 'name',
          width: '150px'
        },
        {
          sortable: false,
          text: this.$t('department'),
          value: 'department',
          width: '150px'
        },
        {
          sortable: false,
          text: this.$t('manager'),
          value: 'manager',
          width: '150px'
        },
        {
          sortable: true,
          text: this.$t('total'),
          name: 'total',
          value: 'total_expense',
          width: '150px'
        },
        ...[...new Set(headers)].map(key => ({
          sortable: true,
          text: key,
          value: `${key}_expense`,
          width: '150px'
        }))
      ]
    },
    dateMin () {
      const [startDate, endDate] = this.date
      if (this.isAbnormalStatistics || (startDate && endDate)) return undefined
      if (startDate) {
        const monthTime = 31 * 60 * 60 * 24 * 1000
        const time = startDate.getTime() - monthTime
        return new Date(time).toISOString()
      } 
      return undefined
    },
    dateMax () {
      const [startDate, endDate] = this.date
      if (this.isAbnormalStatistics) return undefined
      const now = new Date(new Date().getTime() - 60 * 60 * 24 * 1000)
      if (startDate && endDate) return now.toISOString()
      if (startDate) {
        const monthTime = 31 * 60 * 60 * 24 * 1000
        const time = startDate.getTime() + monthTime
        return now.getTime() < time ? now.toISOString() : new Date(time).toISOString()
      } 
      return now.toISOString()
    },
    ...mapGetters([
      'systemBalances'
    ])
  },
  methods: {
    toMoney,
    formatTableCell (item, row, type = '') {
      const result = item[`${row.name || row.text}_${type}`] || 0
      return type === 'expense'
        ? toMoney(result).toLocaleString()
        : result
    },
    async getData () {
      if (!this.isAbnormalStatistics) {
        try {
          let startDate = this.date[0]
          let endDate = this.date[1]
          if (!this.isDate) {
            const curYearMonth = this.month.toISOString().slice(0, 7)
            const [, month] = curYearMonth.split('-')
            const curMonthStartDate = new Date(curYearMonth)
            const curMonthEndDate = new Date(new Date(curYearMonth).setMonth(month) - 1)
            startDate = curMonthStartDate
            endDate = curMonthEndDate
          }
          this.loading = true
          const data = await this.$store.dispatch('getDailyStatistics', {
            startDate,
            endDate
          })
          this.statistics = data
        } catch (err) {
          alert(err.message)
          this.loading = false
        }
      } else {
        this.$store.dispatch('loadStatistics')
      }
    },
    getCommonOptions ({ title, seriesName = '', data = [] }) {
      return {
        title: {
          text: this.$t(title),
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: data => `${data.name} : ${toMoney(data.value)} (${data.percent}%)`
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'center'
        },
        series: [
          {
            name: this.$t(seriesName),
            type: 'pie',
            radius: '50%',
            label: {
              show: true,
              position: 'inside',
              formatter: '{d}%'
            },
            selectedMode: 'single',
            data,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            top: '-15%',
            left: '-10%',
            height: '130%',
            width: '130%'
          }
        ]
      }
    },
    sortData (data, type = '') {
      if (!type) return data
      return [...data].sort((a, b) => b[type] - a[type])
    },
    getRankAndOtherData (allData, type) {
      if (!type || !allData.length) return allData
      const rankData = allData.slice(0, 10).map(item => {
        return {
          value: item[type],
          name: item.clientName
        }
      })
      const otherData = allData.slice(10, allData.length).reduce(
        (obj, item) => {
          obj.value += item[type]
          return obj
        },
        {
          value: 0,
          name: this.$t('other')
        }
      )
      return [...rankData, otherData]
    },
    sortExportData (data) {
      const { sortBy, descending } = this.pagination
      if (!sortBy || descending === null) return data
      const sortData = [...data].sort((a, b) => {
        return (a[sortBy] - b[sortBy]) * (descending ? -1 : 1)
      })
      return sortData
    },
    exportFile () {
      const [clientName, department, manager, ...items] = this.headers
      const otherItemsHeaderOptions = items.reduce(
        (arr, item) => {
          const name = item.text
          return [
            ...arr,
            ...this.dataTypes.map(row => ({
              label: `${name} ${row}`,
              name,
              type: row
            }))
          ]
        },
        []
      )
      const headers = [
        clientName.text,
        department.text,
        manager.text,
        ...otherItemsHeaderOptions.map(item => item.label)
      ]
      const xlsxData = [headers]
      this.sortExportData(this.targetData).forEach(item => {
        const { clientName, department, manager } = item
        const result = [
          clientName,
          department,
          manager
        ]
        otherItemsHeaderOptions.forEach(row => {
          let { name, type } = row
          if (name === this.$t('total')) name = 'total'
          const value = item[`${name}_${type}`] || 0
          result.push(value)
        })
        xlsxData.push(result)
      })
      const fileDate = this.isDate
        ? `${toPickerDateString(this.date[0])} ~ ${toPickerDateString(this.date[1])}`
        : `${this.month.getMonth() + 1}月`
      exportExcelFile({
        fileName: `statistics-${fileDate}.csv`,
        data: xlsxData
      })
    },
    getRangeDate (
      range = 0,
      curDate = new Date()
    ) {
      range = range || 0
      const year = curDate.getFullYear()
      const month = curDate.getMonth()
      const day = curDate.getDate()
      const targetStartDate = new Date(year, month, day - range + 2)
      return [targetStartDate, curDate]
    },
    setDate (day) {
      const dates = (day > -1 ? this.getRangeDate(day) : this.getAllRangeDate())
        .map(date => new Date(date.getTime() - 60 * 60 * 24 * 1000))
      if (!this.dateMax && !this.dateMin) this.date = dates
      this.date = dates.map(date => {
        const time = date.getTime() 
        const maxDate = new Date(this.dateMax)
        const minDate = new Date(this.minDate)
        if (time > maxDate.getTime()) return maxDate
        if (time < minDate.getTime()) return minDate
        return date
      })
    }
  },
  created () {
    this.getData()
    this.$store.dispatch('getAllSystemBalances')
  }
}
</script>

<style scoped>
  .chart {
    height: 300px;
  }

  .table >>> .v-datatable {
    table-layout: fixed;
  }
</style>



// WEBPACK FOOTER //
// src/components/Statistics.vue